import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  buildUrl, buildThumbUrl, buildSizedUrl, COPY_SIZES,
  isResizable, canShowInGrid, fileKind, fileExt, formatSize, formatDay, usageText, copyText,
} from "../media";
import {
  FolderGlyph, IcoCopy, IcoCheck, IcoPencil, IcoTrash, IcoEye, IcoGlobe, IcoLock,
  IcoFileText, IcoFilm, IcoPlay, IcoSort, IcoImage, IcoUpload, IcoSearch, IcoDownload,
} from "./Icons";
import PreviewModal from "./PreviewModal";

const SORTS = [
  { key: "newest", label: "Mới nhất" },
  { key: "oldest", label: "Cũ nhất" },
  { key: "name", label: "Tên A → Z" },
  { key: "size", label: "Dung lượng lớn nhất" },
];

const SORTERS = {
  newest: (a, b) => (b.lastModified || 0) - (a.lastModified || 0),
  oldest: (a, b) => (a.lastModified || 0) - (b.lastModified || 0),
  name: (a, b) => a.name.localeCompare(b.name, "vi", { numeric: true, sensitivity: "base" }),
  size: (a, b) => (b.size || 0) - (a.size || 0),
};

const byName = (a, b) => a.localeCompare(b, "vi", { numeric: true, sensitivity: "base" });

// Thư mục có thể chứa hàng trăm file. Chỉ dựng từng đợt để trang không phải vẽ
// và tải ảnh thu nhỏ cho tất cả cùng lúc; cuộn gần tới cuối thì dựng đợt kế tiếp.
const PAGE_SIZE = 60;
// Ảnh thu nhỏ chưa có (máy chủ đang bận tạo) thì chờ rồi thử lại đúng một lần.
const THUMB_RETRY_MS = 8000;

/* ── Hình xem trước trong một ô file ──
   Không bao giờ tải file gốc nặng vào lưới: JPG/PNG dùng ảnh thu nhỏ của máy
   chủ; video và PDF chỉ hiện biểu tượng (video chỉ được tải khi mở xem). */
function FileThumb({ path, item, kind }) {
  const [state, setState] = useState("loading"); // loading | waiting | ok | failed
  const [attempt, setAttempt] = useState(0);
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  if (kind === "pdf") {
    return <span className="type-tile pdf"><IcoFileText size={38} strokeWidth={1.3} /></span>;
  }
  if (kind === "video") {
    return (
      <span className="type-tile video">
        <IcoFilm size={38} strokeWidth={1.3} />
        <span className="play-badge"><IcoPlay size={16} /></span>
      </span>
    );
  }
  if (!canShowInGrid(item)) {
    return (
      <span className="type-tile image">
        <IcoImage size={34} strokeWidth={1.3} />
        <small>Ảnh lớn · bấm để xem</small>
      </span>
    );
  }
  if (state === "failed") {
    return (
      <span className="type-tile image">
        <IcoImage size={34} strokeWidth={1.3} />
        <small>Chưa có ảnh xem trước</small>
      </span>
    );
  }
  if (state === "waiting") return <span className="type-tile waiting" />;

  const base = isResizable(item.name) ? buildThumbUrl(path, item.name) : buildUrl(path, item.name);
  return (
    <img
      key={attempt}
      src={attempt ? `${base}?thu-lai=${attempt}` : base}
      loading="lazy"
      decoding="async"
      alt=""
      className={state === "ok" ? "is-loaded" : ""}
      onLoad={() => setState("ok")}
      onError={() => {
        if (attempt >= 1) { setState("failed"); return; }
        setState("waiting");
        timer.current = setTimeout(() => { setAttempt(1); setState("loading"); }, THUMB_RETRY_MS);
      }}
    />
  );
}

/* ══════════════════════════════════════════════════ */
export default function ExplorerView({
  role, folders = [], images = [], path, search = "",
  onOpenFolder,
  onDeleteFolder, onDeleteImage,
  onRenameFolder, onRenameImage,
  onUploadClick, onNotify,
  usage = { files: {}, folders: {} },
  lockedFolders = [], manageableFolders = [], onManageLock,
}) {
  const [renameTarget, setRenameTarget] = useState(null);
  const [newName, setNewName]           = useState("");
  const [copied, setCopied]             = useState(null);
  const [copyMenu, setCopyMenu]         = useState(null); // tên file đang mở menu chọn cỡ
  const [sort, setSort]                 = useState("newest");
  const [previewName, setPreviewName]   = useState(null);
  const [limit, setLimit]               = useState(PAGE_SIZE);
  const moreRef = useRef(null);

  const keyword = (search || "").trim().toLowerCase();

  // Đổi thư mục thì đóng mọi thứ đang mở dở của thư mục cũ.
  useEffect(() => {
    setRenameTarget(null);
    setCopyMenu(null);
    setPreviewName(null);
  }, [path]);

  // Đổi thư mục, từ khóa lọc hay cách sắp xếp thì quay về đợt đầu.
  useEffect(() => { setLimit(PAGE_SIZE); }, [path, keyword, sort]);

  // Menu chọn cỡ: bấm ra ngoài hoặc nhấn Esc để đóng.
  useEffect(() => {
    if (!copyMenu) return undefined;
    const onDown = (e) => {
      if (!e.target.closest(".copy-menu") && !e.target.closest("[data-copy-trigger]")) setCopyMenu(null);
    };
    const onKey = (e) => { if (e.key === "Escape") setCopyMenu(null); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [copyMenu]);

  const askRename = (name) => { setRenameTarget(name); setNewName(name); };

  const confirmRename = () => {
    if (!newName.trim()) return;
    if (newName === renameTarget) { setRenameTarget(null); return; }
    const isFile = images.some((i) => i.name === renameTarget);
    if (isFile) onRenameImage(renameTarget, newName);
    else if (role === "admin") onRenameFolder(renameTarget, newName);
    setRenameTarget(null);
  };

  const renameKeys = (e) => {
    if (e.key === "Enter") confirmRename();
    if (e.key === "Escape") setRenameTarget(null);
  };

  // key: giá trị dùng để hiện dấu "đã chép" trên nút; label: tên cỡ đưa vào thông báo.
  const copyLink = async (url, key, label) => {
    const ok = await copyText(url);
    if (!ok) { onNotify?.("Không chép được link, hãy chép tay từ thanh địa chỉ", "error"); return; }
    setCopied(key);
    setTimeout(() => setCopied((c) => (c === key ? null : c)), 1800);
    onNotify?.(label ? `Đã chép link ${label}` : "Đã chép link");
  };

  const shownFolders = useMemo(
    () => folders.filter((f) => f.toLowerCase().includes(keyword)).sort(byName),
    [folders, keyword]
  );

  const shownFiles = useMemo(
    () => images
      .filter((i) => i?.name && i.name.toLowerCase().includes(keyword))
      .sort(SORTERS[sort]),
    [images, keyword, sort]
  );

  const visibleFiles = shownFiles.length > limit ? shownFiles.slice(0, limit) : shownFiles;
  const remaining = shownFiles.length - visibleFiles.length;

  // Cuộn gần tới cuối lưới thì dựng thêm một đợt.
  useEffect(() => {
    const el = moreRef.current;
    if (!el || remaining <= 0 || typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver(
      (entries) => { if (entries.some((e) => e.isIntersecting)) setLimit((l) => l + PAGE_SIZE); },
      { rootMargin: "500px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [remaining, limit]);

  // PDF mở ở thẻ mới nên không nằm trong chuỗi xem lớn (mũi tên trái/phải bỏ qua PDF).
  const previewItems = useMemo(
    () => shownFiles.filter((i) => fileKind(i.name) !== "pdf"),
    [shownFiles]
  );
  const previewIndex = previewName ? previewItems.findIndex((i) => i.name === previewName) : -1;

  const openFile = (item) => {
    const url = buildUrl(path, item.name);
    if (fileKind(item.name) === "pdf") window.open(url, "_blank", "noopener");
    else setPreviewName(item.name);
  };

  const downloadFile = (name) => {
    const a = document.createElement("a");
    a.href = buildUrl(path, name);
    a.download = name;
    a.click();
  };

  const nothingAtAll = folders.length === 0 && images.length === 0;
  const nothingMatches = !nothingAtAll && shownFolders.length === 0 && shownFiles.length === 0;

  return (
    <div className="explorer">
      {/* ── Thư mục trống hẳn ── */}
      {nothingAtAll && (
        <div className="empty">
          <span className="empty-art"><IcoImage size={34} strokeWidth={1.4} /></span>
          <h3>Thư mục này đang trống</h3>
          <p>Tải ảnh, video hoặc PDF lên để bắt đầu.</p>
          {onUploadClick && (
            <button className="btn btn-primary" onClick={onUploadClick}>
              <IcoUpload size={17} /> <span>Tải lên</span>
            </button>
          )}
        </div>
      )}

      {/* ── Lọc không ra kết quả ── */}
      {nothingMatches && (
        <div className="empty">
          <span className="empty-art"><IcoSearch size={30} strokeWidth={1.4} /></span>
          <h3>Không có mục nào khớp “{search.trim()}”</h3>
          <p>Ô lọc chỉ tìm trong thư mục đang mở.</p>
        </div>
      )}

      {/* ── THƯ MỤC ── */}
      {shownFolders.length > 0 && (
        <section className="block">
          <div className="block-head">
            <h2>Thư mục</h2>
            <span className="count">{shownFolders.length}</span>
          </div>

          <div className="folder-grid">
            {shownFolders.map((f) => {
              const folderUrl = buildUrl(path, f);
              const renaming = renameTarget === f;
              const usedCount = usage.folders[f] || 0;
              const hasLock = lockedFolders.includes(f);
              const canManageLock = !!onManageLock && manageableFolders.includes(f);

              return (
                <div
                  key={f}
                  className={`folder-tile ${renaming ? "is-renaming" : ""} ${hasLock ? "has-lock" : ""}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => { if (!renaming) onOpenFolder(f); }}
                  onKeyDown={(e) => { if (e.key === "Enter" && !renaming && e.target === e.currentTarget) onOpenFolder(f); }}
                >
                  <span className="folder-ico">
                    <FolderGlyph size={38} />
                    {hasLock && <span className="folder-lock" title="Thư mục có mật khẩu"><IcoLock size={11} strokeWidth={2.2} /></span>}
                  </span>

                  {renaming ? (
                    <div className="rename-box" onClick={(e) => e.stopPropagation()}>
                      <input
                        className="rename-input"
                        value={newName}
                        autoFocus
                        onChange={(e) => setNewName(e.target.value)}
                        onKeyDown={renameKeys}
                      />
                      <div className="rename-actions">
                        <button className="btn btn-primary btn-xs" onClick={confirmRename}>Lưu</button>
                        <button className="btn btn-quiet btn-xs" onClick={() => setRenameTarget(null)}>Hủy</button>
                      </div>
                    </div>
                  ) : (
                    <span className="folder-body">
                      <strong className="folder-name" title={f}>{f}</strong>
                      {usedCount > 0 ? (
                        <span className="folder-sub used" title="Số ảnh trong thư mục này đang được website hoặc app sử dụng">
                          <IcoGlobe size={12} /> {usedCount} ảnh đang dùng
                        </span>
                      ) : hasLock ? (
                        <span className="folder-sub locked">Có mật khẩu</span>
                      ) : (
                        <span className="folder-sub">Thư mục</span>
                      )}
                    </span>
                  )}

                  {!renaming && (
                    <span className="tile-actions" onClick={(e) => e.stopPropagation()}>
                      <button
                        className={`mini-btn ${copied === folderUrl ? "ok" : ""}`}
                        onClick={() => copyLink(folderUrl, folderUrl, "thư mục")}
                        title="Sao chép link thư mục"
                        aria-label={`Sao chép link thư mục ${f}`}
                      >
                        {copied === folderUrl ? <IcoCheck size={15} /> : <IcoCopy size={15} />}
                      </button>
                      {canManageLock && (
                        <button
                          className="mini-btn"
                          onClick={() => onManageLock(f)}
                          title={hasLock ? "Đổi hoặc gỡ mật khẩu" : "Đặt mật khẩu"}
                          aria-label={`${hasLock ? "Đổi hoặc gỡ mật khẩu" : "Đặt mật khẩu"} thư mục ${f}`}
                        >
                          <IcoLock size={15} />
                        </button>
                      )}
                      {role === "admin" && (
                        <>
                          <button className="mini-btn" onClick={() => askRename(f)} title="Đổi tên" aria-label={`Đổi tên thư mục ${f}`}>
                            <IcoPencil size={15} />
                          </button>
                          {onDeleteFolder && (
                            <button className="mini-btn danger" onClick={() => onDeleteFolder(f)} title="Chuyển vào thùng rác" aria-label={`Chuyển thư mục ${f} vào thùng rác`}>
                              <IcoTrash size={15} />
                            </button>
                          )}
                        </>
                      )}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* ── FILE ── */}
      {shownFiles.length > 0 && (
        <section className="block">
          <div className="block-head">
            <h2>Hình ảnh, video, PDF</h2>
            <span className="count">{shownFiles.length}</span>
            <label className="sort">
              <IcoSort size={15} />
              <select value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Sắp xếp">
                {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </label>
          </div>

          <div className="file-grid">
            {visibleFiles.map((item) => {
              const url = buildUrl(path, item.name);
              const kind = fileKind(item.name);
              const used = usage.files[item.name];
              const canResize = isResizable(item.name);
              const isCopied = copied === url;
              const renaming = renameTarget === item.name;

              return (
                <div key={item.name} className={`file-card ${copyMenu === item.name ? "menu-open" : ""}`}>
                  <div className={`file-thumb kind-${kind}`}>
                    <button
                      className="file-open"
                      onClick={() => openFile(item)}
                      title={kind === "pdf" ? "Mở PDF trong thẻ mới" : kind === "video" ? "Phát video" : "Xem lớn"}
                      aria-label={`Mở ${item.name}`}
                    >
                      <FileThumb path={path} item={item} kind={kind} />
                    </button>

                    <span className={`type-chip kind-${kind}`}>{fileExt(item.name)}</span>

                    {used && (
                      <span className="used-chip" title={usageText(used)}>
                        <IcoGlobe size={12} /> Đang dùng
                      </span>
                    )}

                    <div className="file-actions">
                      {kind !== "pdf" && (
                        <button className="mini-btn solid" onClick={() => openFile(item)} title={kind === "video" ? "Phát video" : "Xem lớn"}>
                          <IcoEye size={15} />
                        </button>
                      )}
                      {kind === "pdf" && (
                        <button className="mini-btn solid" onClick={() => downloadFile(item.name)} title="Tải PDF về máy">
                          <IcoDownload size={15} />
                        </button>
                      )}
                      <button
                        data-copy-trigger
                        className={`mini-btn solid ${isCopied ? "ok" : ""}`}
                        onClick={() => {
                          if (canResize) setCopyMenu(copyMenu === item.name ? null : item.name);
                          else copyLink(url, url);
                        }}
                        title={canResize ? "Sao chép link (chọn cỡ ảnh)" : "Sao chép link"}
                      >
                        {isCopied ? <IcoCheck size={15} /> : <IcoCopy size={15} />}
                      </button>
                      {onDeleteImage && (
                        <button className="mini-btn solid danger" onClick={() => onDeleteImage(item.name)} title="Chuyển vào thùng rác">
                          <IcoTrash size={15} />
                        </button>
                      )}
                    </div>
                  </div>

                  {copyMenu === item.name && (
                    <div className="copy-menu" role="menu">
                      <div className="copy-menu-title">Sao chép link cỡ…</div>
                      {COPY_SIZES.map((sz) => (
                        <button
                          key={sz.key}
                          role="menuitem"
                          className="copy-menu-item"
                          onClick={() => { copyLink(buildSizedUrl(path, item.name, sz.width), url, `cỡ ${sz.label.toLowerCase()}`); setCopyMenu(null); }}
                        >
                          <b>{sz.label}</b><span>{sz.hint}</span>
                        </button>
                      ))}
                      <button
                        role="menuitem"
                        className="copy-menu-item"
                        onClick={() => { copyLink(url, url, "ảnh gốc"); setCopyMenu(null); }}
                      >
                        <b>Gốc</b><span>{formatSize(item.size) || "nguyên kích thước"}</span>
                      </button>
                    </div>
                  )}

                  <div className="file-info">
                    {renaming ? (
                      <div className="rename-box">
                        <input
                          className="rename-input"
                          value={newName}
                          autoFocus
                          onChange={(e) => setNewName(e.target.value)}
                          onKeyDown={renameKeys}
                        />
                        <div className="rename-actions">
                          <button className="btn btn-primary btn-xs" onClick={confirmRename}>Lưu</button>
                          <button className="btn btn-quiet btn-xs" onClick={() => setRenameTarget(null)}>Hủy</button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="file-name" title={item.name}>{item.name}</div>
                        <div className="file-meta">
                          <span>
                            {[formatSize(item.size), formatDay(item.lastModified)].filter(Boolean).join(" · ")}
                          </span>
                          <button className="mini-btn rename-btn" onClick={() => askRename(item.name)} title="Đổi tên file" aria-label={`Đổi tên ${item.name}`}>
                            <IcoPencil size={14} />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {remaining > 0 && (
            <div className="load-more" ref={moreRef}>
              <button className="btn btn-quiet" onClick={() => setLimit((l) => l + PAGE_SIZE)}>
                Hiện thêm {Math.min(PAGE_SIZE, remaining)} tệp (còn {remaining})
              </button>
            </div>
          )}
        </section>
      )}

      {previewIndex >= 0 && (
        <PreviewModal
          items={previewItems}
          index={previewIndex}
          path={path}
          usage={usage}
          onNavigate={(i) => setPreviewName(previewItems[i]?.name || null)}
          onClose={() => setPreviewName(null)}
          onCopy={copyLink}
          onDelete={onDeleteImage}
          onDownload={downloadFile}
        />
      )}
    </div>
  );
}
